import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Truck, ShieldCheck, RefreshCcw, Headphones, ArrowRight, ShoppingCart, Star, CheckCircle2 } from 'lucide-react';
import { Product, Banner, HeroFeature } from '../types';
import { useCart } from '../context/CartContext';
import { Link, useNavigate } from 'react-router-dom';

const BannerSection = () => {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const { publicSupabase } = await import('../lib/supabase');
        const { data, error } = await publicSupabase
          .from('banners')
          .select('*')
          .eq('active', true)
          .order('order', { ascending: true });
        
        if (!error && data && data.length > 0) {
          setBanners(data as Banner[]);
        }
      } catch {
      } finally {
        setLoading(false);
      }
    };
    localStorage.removeItem('eg_home_banners_cache');
    fetchBanners();
  }, []);

  useEffect(() => {
    if (banners.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % banners.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [banners]);

  if (loading) {
    return (
      <div className="relative overflow-hidden bg-slate-200 mx-4 md:mx-8 mt-6 rounded-[2.5rem] shadow-sm animate-pulse aspect-[21/9]">
      </div>
    );
  }

  if (banners.length === 0) return null;

  return (
    <div className="relative overflow-hidden bg-slate-50 mx-4 md:mx-8 mt-6 rounded-[2.5rem] shadow-2xl grid aspect-[21/9]">
      <AnimatePresence>
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1 }}
          className="col-start-1 row-start-1 w-full"
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.8}
          onDragEnd={(e, { offset }) => {
            const swipePower = Math.abs(offset.x);
            if (swipePower > 50) {
              if (offset.x < 0) {
                // Swipe Left => Next Image
                setCurrentIndex(prev => (prev + 1) % banners.length);
              } else {
                // Swipe Right => Prev Image
                setCurrentIndex(prev => (prev - 1 + banners.length) % banners.length);
              }
            }
          }}
        >
          <img
            src={banners[currentIndex].image}
            className="w-full h-full object-cover pointer-events-none"
            alt="Banner"
            width={2100}
            height={900}
            decoding="async"
            fetchPriority="high"
            loading="eager"
            sizes="(max-width: 768px) 100vw, 92vw"
          />
        </motion.div>
      </AnimatePresence>
      
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10 pointer-events-none">
        {banners.map((_, i) => (
          <div 
            key={i} 
            className={`transition-all rounded-full ${i === currentIndex ? 'w-6 h-1.5 bg-blue-600 shadow-md' : 'w-1.5 h-1.5 bg-white shadow-sm'}`}
          />
        ))}
      </div>
    </div>
  );
};

const Features = () => {
  const [features, setFeatures] = useState<HeroFeature[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { publicSupabase } = await import('../lib/supabase');
        const { data, error } = await publicSupabase.from('hero_features').select('*');
        if (!error && data && data.length > 0) {
          setFeatures(data as HeroFeature[]);
          return;
        }
      } catch {}
    };
    fetchData();
  }, []);

  const IconMap: Record<string, any> = { Truck, ShieldCheck, RefreshCcw, CheckCircle2, Headphones };
  const ColorMap: Record<string, string> = { 
    'Truck': 'bg-blue-50 text-blue-600', 
    'ShieldCheck': 'bg-green-50 text-green-600', 
    'RefreshCcw': 'bg-purple-50 text-purple-600', 
    'CheckCircle2': 'bg-purple-50 text-purple-600', 
    'Headphones': 'bg-orange-50 text-orange-600' 
  };

  return (
    <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 px-4 md:px-12 py-16 border-b border-slate-100">
      {features.map((f, i) => {
        // Map old icons to new if exists
        let iconName = f.icon;
        if (f.title.includes('Support')) iconName = 'Headphones';
        if (f.title.includes('Return') || f.title.includes('Cash') || f.title.includes('Delivery') && !f.title.includes('Fast Delivery')) iconName = 'CheckCircle2';
        if (f.title.includes('Fast Delivery')) iconName = 'Truck';
        if (f.title.includes('Trusted') || f.title.includes('Secure')) iconName = 'ShieldCheck';
        
        const Icon = IconMap[iconName] || IconMap[f.icon] || Truck;
        const colorClass = ColorMap[iconName] || ColorMap[f.icon] || 'bg-slate-50 text-slate-600';
        
        return (
          <div key={i} className="flex flex-col items-center justify-center gap-3 w-full">
            <div className={`w-14 h-14 rounded-full flex items-center justify-center ${colorClass}`}>
              <Icon className="w-6 h-6" />
            </div>
            <div className="text-center">
              <div className="text-[13px] font-medium tracking-tight text-slate-800">{f.title}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const ProductGrid = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const { addToCart, toggleCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const { publicSupabase } = await import('../lib/supabase');
        const { data, error } = await publicSupabase
          .from('products')
          .select('*')
          .eq('featured', true)
          .limit(8);
        
        if (!error && data && data.length > 0) {
          setProducts(data as Product[]);
        }
      } catch {
      } finally {
        setLoading(false);
      }
    };
    localStorage.removeItem('eg_home_featured_products_cache');
    fetchProducts();
  }, []);

  return (
    <section className="max-w-7xl mx-auto px-4 md:px-8 py-12">
      <div className="flex items-center justify-between mb-10">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Featured Products</h2>
        </div>
        <Link to="/shop" className="text-sm font-bold flex items-center gap-1 hover:underline">View All <ArrowRight className="w-4 h-4" /></Link>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-3xl border border-slate-100 overflow-hidden shadow-sm animate-pulse">
              <div className="aspect-square bg-slate-200"></div>
              <div className="p-4 space-y-3">
                <div className="h-3 bg-slate-200 rounded w-3/4"></div>
                <div className="h-4 bg-slate-200 rounded w-1/4"></div>
                <div className="h-8 bg-slate-200 rounded-xl w-full mt-3"></div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {products.map(product => (
            <motion.div 
              key={product.id} 
              layout
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="group relative flex flex-col bg-slate-50 border border-slate-100 rounded-[2rem] p-4 md:p-6 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_40px_-15px_rgba(0,0,0,0.05)] h-full"
            >
              <Link to={`/product/${product.slug}`} className="block relative z-10 mb-4 h-full flex flex-col focus:outline-none">
                <div className="aspect-[4/5] relative mb-6 rounded-2xl overflow-hidden bg-white shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)]">
                  <img 
                    src={product.images?.[0] || (product as any).image} 
                    alt={product.name} 
                    className="absolute w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" 
                    width={500}
                    height={625}
                    loading="lazy"
                    decoding="async"
                  />
                  {product.sale_price && (
                    <span className="absolute top-3 right-3 bg-rose-500/90 backdrop-blur-md text-white text-[10px] font-black px-2.5 py-1 rounded-full shadow-[0_4px_12px_rgba(225,29,72,0.2)] border border-rose-400/50 tracking-widest z-10 flex items-center justify-center">
                      -{Math.abs(Math.round(((product.price - product.sale_price) / product.price) * 100))}%
                    </span>
                  )}
                  {!product.delivery_charge_enabled && (
                    <span className="absolute top-3 left-3 z-10 flex max-w-[calc(100%-5rem)] items-center gap-1.5 rounded-full border border-emerald-200 bg-white/95 px-2.5 py-1.5 text-[9px] font-black uppercase tracking-wide text-emerald-700 shadow-[0_8px_18px_rgba(16,185,129,0.18)] backdrop-blur-md sm:text-[10px]">
                      <Truck className="h-3.5 w-3.5 flex-none text-emerald-600" />
                      <span className="truncate">
                        Free<span className="hidden sm:inline"> Delivery</span>
                      </span>
                    </span>
                  )}
                </div>

                <div className="space-y-3 mt-auto">
                  <h3 className="text-sm font-bold text-slate-900 leading-tight group-hover:text-slate-600 transition-colors line-clamp-2 min-h-[2.5rem]">{product.name}</h3>
                  <div className="flex flex-col gap-1 pt-1">
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-black tracking-tight text-slate-900">৳{product.sale_price || product.price}</span>
                      {product.sale_price && <span className="text-[11px] font-semibold text-slate-600 line-through">৳{product.price}</span>}
                    </div>
                  </div>
                </div>
              </Link>

              <div className="relative z-20 mt-auto pt-2">
                <button 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    let defaultVariants: any = undefined;
                    if (product.variants && product.variants.length > 0) {
                      defaultVariants = {};
                      product.variants.forEach((v: any) => {
                        defaultVariants[v.name] = v.options[0];
                      });
                    }
                    addToCart({ 
                      cartItemId: defaultVariants ? `${product.id}-${Object.values(defaultVariants).join('-')}` : product.id, 
                      id: product.id, 
                      name: product.name, 
                      price: product.sale_price || product.price, 
                      image: product.images?.[0] || (product as any).image, 
                      quantity: 1,
                      variants: defaultVariants,
                      deliveryChargeEnabled: Boolean(product.delivery_charge_enabled)
                    });
                    toggleCart();
                  }}
                  className="w-full py-3.5 bg-slate-900 text-white text-[11px] font-bold rounded-xl hover:bg-slate-800 transition-colors uppercase tracking-widest shadow-[0_4px_14px_rgba(0,0,0,0.1)] flex items-center justify-center gap-2 active:scale-95"
                >
                  <ShoppingCart className="w-4 h-4" />
                  Add to Cart
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </section>
  );
};

export default function Home() {
  return (
    <div className="pb-20">
      <BannerSection />
      <Features />
      <ProductGrid />
    </div>
  );
}
